import { createContext, ReactNode, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'
import { useSelf } from '../../api/self'
import { SelfMe } from '../../api/self/me'
import { useQueryEither } from '../../hooks/useQueryEither'
import { LocalStorage } from '../../packages/storage/localStorage'
import { paths } from '../../routes/paths'

interface State {
  authorized: boolean
  token: string
  me?: SelfMe | undefined
}

type Action =
  | {
      type: '@auth/set'
      payload: State
    }
  | {
      type: '@auth/set-user'
      payload: SelfMe
    }
  | {
      type: '@auth/unauthorize'
    }

type Context = {
  dispatch: React.Dispatch<Action>
  state: State
}

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case '@auth/set': {
      return action.payload
    }

    case '@auth/set-user': {
      return { ...state, me: action.payload }
    }

    case '@auth/unauthorize': {
      return {
        authorized: false,
        token: '',
      }
    }

    default: {
      return state
    }
  }
}

const initialState = { authorized: false, token: '' }

const AuthContext = createContext<Context>({ dispatch: () => ({}), state: initialState })

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [initialized, setInitialized] = useState(false)
  const [state, dispatch] = useReducer(reducer, initialState)
  const self = useSelf()
  const history = useHistory()

  const { data: token } = useQuery(
    'token',
    async () => {
      const storage = await LocalStorage.getItem('budget-flow-store')

      return storage.get('authToken')
    },
    {
      onSettled: t => {
        if (!t) {
          history.replace(paths.login)
        }
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      suspense: true,
    }
  )

  const { isSuccess, data: user } = useQueryEither({
    config: {
      enabled: !!token && !initialized,
      onError: () => {
        history.replace(paths.login)
      },
      refetchOnReconnect: false,
      retry: false,
      suspense: true,
    },
    key: 'fetchMe',
    fn: self.fetchMe,
  })

  useEffect(() => {
    setInitialized(currentInitialized => {
      if (isSuccess && !currentInitialized) {
        dispatch({ payload: { authorized: true, token: token!, me: user }, type: '@auth/set' })
      }

      return true
    })
  }, [user, isSuccess, token])

  return <AuthContext.Provider value={useMemo(() => ({ state, dispatch }), [state])}>{children}</AuthContext.Provider>
}

export const useAuth = () => useContext(AuthContext)
